import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Download, KeyRound, Loader, X } from "lucide-react";
import { buildWhatsAppUrl } from "../../utils/whatsapp";

/**
 * Pide la clave de la entrega antes de entregar el enlace de descarga.
 *
 * El enlace no está en el sitio: lo devuelve /.netlify/functions/descarga
 * solo con la clave correcta (ver ese archivo). Con la clave buena:
 *
 *   - se muestra un botón «Abrir mis fotos». No se abre la pestaña sola:
 *     después de esperar al servidor, Safari y Chrome en el teléfono
 *     bloquean un window.open que no nace de un toque.
 *   - el enlace se recuerda en este navegador (localStorage, por entrega),
 *     así la próxima vez los botones de descarga llevan directo a la
 *     carpeta. Es el teléfono del cliente; ahí el enlace ya es suyo.
 */

const ENDPOINT = "/.netlify/functions/descarga";
const MENSAJE_SIN_CLAVE = "Hola Omega, no encuentro la clave para descargar mis fotos.";

const claveLocal = (entrega) => `omega:descarga:${entrega}`;

export const enlaceRecordado = (entrega) => {
  try {
    return window.localStorage.getItem(claveLocal(entrega)) || "";
  } catch {
    return "";
  }
};

const recordarEnlace = (entrega, url) => {
  try {
    window.localStorage.setItem(claveLocal(entrega), url);
  } catch {
    /* Sin almacenamiento habrá que poner la clave cada vez. */
  }
};

const DescargaModal = ({ entrega, abierto, onCerrar, onDesbloqueada }) => {
  const [clave, setClave] = useState("");
  const [estado, setEstado] = useState({ tipo: null, mensaje: "" });
  const [enviando, setEnviando] = useState(false);
  const [url, setUrl] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;
    setEstado({ tipo: null, mensaje: "" });
    const foco = window.setTimeout(() => inputRef.current?.focus(), 50);
    const alTeclear = (event) => {
      if (event.key === "Escape") onCerrar();
    };
    document.addEventListener("keydown", alTeclear);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.clearTimeout(foco);
      document.removeEventListener("keydown", alTeclear);
      document.body.style.overflow = overflow;
    };
  }, [abierto, onCerrar]);

  if (!abierto) return null;

  const enviar = async (event) => {
    event.preventDefault();
    if (!clave.trim()) {
      setEstado({ tipo: "error", mensaje: "Escribe la clave que te enviamos." });
      return;
    }
    setEnviando(true);
    setEstado({ tipo: null, mensaje: "" });
    try {
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entrega, clave }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) {
        setEstado({ tipo: "error", mensaje: "Esa clave no es correcta. Revísala e inténtalo de nuevo." });
      } else if (response.status === 429) {
        setEstado({ tipo: "error", mensaje: "Demasiados intentos. Espera unos minutos y vuelve a probar." });
      } else if (!response.ok) {
        throw new Error(data.error || "Error");
      } else if (data.pendiente) {
        setEstado({
          tipo: "aviso",
          mensaje: "La clave es correcta, pero tus fotos en alta resolución todavía no están listas. Te avisamos apenas lo estén.",
        });
      } else if (data.url) {
        recordarEnlace(entrega, data.url);
        setUrl(data.url);
        if (onDesbloqueada) onDesbloqueada(data.url);
      }
    } catch {
      setEstado({ tipo: "error", mensaje: "No pudimos revisar la clave. Inténtalo de nuevo en un momento." });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-navy-950/70 p-4 backdrop-blur-sm sm:items-center"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCerrar();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="descarga-titulo"
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lift sm:p-8"
      >
        <div className="flex items-start justify-between gap-4">
          <h2 id="descarga-titulo" className="font-display text-xl font-bold text-navy-900">
            {url ? "Tus fotos están listas" : "Descarga tus fotos"}
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="-mr-2 -mt-1 rounded-lg p-2 text-navy-400 hover:bg-navy-50 hover:text-navy-900"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {url ? (
          <>
            <p className="mt-3 text-navy-600">
              Se abren en Google Drive, en una pestaña nueva. Desde ahora este navegador recuerda tu
              clave.
            </p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onCerrar}
              className="btn-omega mt-6 w-full justify-center"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Abrir mis fotos
            </a>
          </>
        ) : (
          <form onSubmit={enviar} noValidate>
            <p className="mt-3 text-navy-600">
              Escribe la clave que te enviamos junto con el enlace de tu entrega.
            </p>
            <label htmlFor="descarga-clave" className="mt-5 block text-sm font-semibold text-navy-900">
              Clave
            </label>
            <div className="relative mt-2">
              <KeyRound
                className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-300"
                aria-hidden="true"
              />
              <input
                ref={inputRef}
                id="descarga-clave"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={64}
                value={clave}
                onChange={(e) => setClave(e.target.value)}
                className="w-full rounded-xl border border-navy-200 bg-white py-3 pl-11 pr-4 font-mono text-lg uppercase tracking-widest text-navy-900 outline-none transition focus:border-navy-500"
              />
            </div>

            {estado.tipo ? (
              <div
                className={`mt-4 flex items-start gap-2 text-sm ${
                  estado.tipo === "error" ? "text-red-700" : "text-navy-600"
                }`}
                role={estado.tipo === "error" ? "alert" : "status"}
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <p>{estado.mensaje}</p>
              </div>
            ) : null}

            <button type="submit" disabled={enviando} className="btn-omega mt-6 w-full justify-center">
              {enviando ? (
                <Loader className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="h-4 w-4" aria-hidden="true" />
              )}
              {enviando ? "Revisando…" : "Ver mis fotos"}
            </button>
            <p className="mt-4 text-center text-sm text-navy-500">
              ¿No tienes la clave?{" "}
              <a
                href={buildWhatsAppUrl(MENSAJE_SIN_CLAVE)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-navy-900 underline decoration-sand-400 underline-offset-4"
              >
                Escríbenos por WhatsApp
              </a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default DescargaModal;
